import { onUnmounted } from 'vue';

import type {
  MessageAttachmentDTO,
  MessageAttachmentUrlDTO,
  SignedUrlDTO,
} from '@nora-monorepo/nora-api-client';
import { useApiStore } from '@nora-monorepo/ui-shared';

import { useConversationStore } from '#nora-chat-ui/stores/conversation/conversation-store';

/** The default max amount of retries before giving up refreshing an attachment. */
const DEFAULT_MAX_REFRESH_RETRIES = 3;

/** Default value for how long before URL expiry we trigger a refresh (in milliseconds). */
const DEFAULT_REFRESH_BUFFER_MS = 60 * 1000;

interface WatchedAttachment {
  conversationId: string;
  messageId: string;
  attachment: MessageAttachmentDTO;
  abortController: AbortController | null;
  timerId: ReturnType<typeof setTimeout> | null;

  /** How many times we have retried so far. */
  retryCount: number;

  /** The amount of references this attachment has, when it has no references it is removed from being watched. */
  refCount: number;
}

export interface AttachmentIdentifier {
  conversationId: string;
  attachmentId: string;
}

export interface WatchAttachmentParams {
  conversationId: string;
  messageId: string;
  attachment: MessageAttachmentDTO;
}

export interface UnwatchAttachmentParams {
  attachmentId: string;
}

interface FetchFreshUrlsParams extends AttachmentIdentifier {
  needsDownload: boolean;
  needsThumbnail: boolean;
  signal?: AbortSignal;
}

/**
 * Module-level singleton state for watching attachments across all component instances.
 *
 * This is intentionally a singleton to:
 * 1. Share refresh timers across components displaying the same attachment
 * 2. Deduplicate concurrent refresh requests for the same attachment
 * 3. Maintain a single source of truth for attachment URL expiry tracking
 *
 * Each instance maintains its own `attachmentIds` Set to track which
 * attachments it registered, enabling proper cleanup when the component unmounts.
 */
const watchedAttachments = new Map<string, WatchedAttachment>();
const pendingRefreshes = new Map<string, Promise<MessageAttachmentUrlDTO>>();

/**
 * Checks if an attachment has any URLs that need to be watched/refreshed.
 */
function hasRefreshableUrls(attachment: MessageAttachmentDTO): boolean {
  return !!(attachment.urls?.download || attachment.urls?.thumbnail);
}

/**
 * Gets the earliest expiry date from an attachment's URLs.
 * Returns null if no URLs have expiry dates.
 */
function getEarliestExpiry(attachment: MessageAttachmentDTO): Date | null {
  const downloadExpiry = attachment.urls?.download?.expiresAt;
  const thumbnailExpiry = attachment.urls?.thumbnail?.expiresAt;

  if (!downloadExpiry && !thumbnailExpiry) {
    return null;
  }

  const downloadTime = downloadExpiry ? new Date(downloadExpiry).getTime() : Infinity;
  const thumbnailTime = thumbnailExpiry ? new Date(thumbnailExpiry).getTime() : Infinity;

  return new Date(Math.min(downloadTime, thumbnailTime));
}

export interface UseAttachmentRefreshOptions {
  /**
   * How long before URL expiry to trigger a refresh (in milliseconds).
   * @default DEFAULT_REFRESH_BUFFER_MS
   */
  refreshBufferMs?: number;
  /**
   * Maximum number of retry attempts when a refresh fails.
   * @default DEFAULT_MAX_REFRESH_RETRIES
   */
  maxRefreshRetries?: number;
}

export interface UseAttachmentUrlRefreshInterface {
  /**
   * Start watching an attachment for automatic URL refresh.
   */
  watchAttachment: (params: WatchAttachmentParams) => void;
  /**
   * Stop watching an attachment (decrements reference count).
   */
  unwatchAttachment: (params: UnwatchAttachmentParams) => void;
  /**
   * Manually trigger a URL refresh for an attachment.
   */
  refreshConversationAttachment: (params: AttachmentIdentifier) => Promise<MessageAttachmentUrlDTO | null>;
  /**
   * Clean up all attachments watched by this instance.
   */
  cleanup: () => void;
}

/**
 * Composable for automatically refreshing attachment URLs before they expire.
 */
export function useAttachmentRefresh(options: UseAttachmentRefreshOptions = {}): UseAttachmentUrlRefreshInterface {
  const {
    refreshBufferMs = DEFAULT_REFRESH_BUFFER_MS,
    maxRefreshRetries = DEFAULT_MAX_REFRESH_RETRIES,
  } = options;

  const api = useApiStore();
  const conversationStore = useConversationStore();
  const attachmentIds = new Set<string>();

  /**
   * Calculates the next time we will refresh the token based on when it expires
   */
  function calculateNextRefreshTime(expiresAt: Date): number {
    const refreshTime = expiresAt.getTime() - refreshBufferMs;
    return Math.max(0, refreshTime - Date.now());
  }

  async function fetchDownloadUrl({
    conversationId,
    attachmentId,
    signal,
  }: AttachmentIdentifier & {
    signal?: AbortSignal;
  }): Promise<SignedUrlDTO | undefined> {
    const response = await api.unwrap(
      api.conversationMessageAttachment.getAttachmentDownloadUrl(
        { conversationId, attachmentId },
        { signal },
      ),
    );
    return response.data as SignedUrlDTO | undefined;
  }

  async function fetchThumbnailUrl({
    conversationId,
    attachmentId,
    signal,
  }: AttachmentIdentifier & {
    signal?: AbortSignal;
  }): Promise<SignedUrlDTO | undefined> {
    const response = await api.unwrap(
      api.conversationMessageAttachment.getAttachmentThumbnailUrl(
        { conversationId, attachmentId },
        { signal },
      ),
    );
    return response.data as SignedUrlDTO | undefined;
  }

  async function fetchFreshUrls({
    conversationId,
    attachmentId,
    needsDownload,
    needsThumbnail,
    signal,
  }: FetchFreshUrlsParams): Promise<MessageAttachmentUrlDTO> {
    const [downloadUrl, thumbnailUrl] = await Promise.all([
      needsDownload ? fetchDownloadUrl({ conversationId, attachmentId, signal }) : undefined,
      needsThumbnail ? fetchThumbnailUrl({ conversationId, attachmentId, signal }) : undefined,
    ]);

    return {
      download: downloadUrl,
      thumbnail: thumbnailUrl,
    };
  }

  /**
   * Returns a pending refresh promise if one exists for this attachment.
   */
  function getPendingRefresh(cacheKey: string): Promise<MessageAttachmentUrlDTO> | null {
    return pendingRefreshes.get(cacheKey) ?? null;
  }

  /**
   * Determines which URL types need to be refreshed for a watched attachment.
   * Returns null if nothing needs to be refreshed.
   */
  function getRefreshNeeds(watchedAttachment: WatchedAttachment): { needsDownload: boolean; needsThumbnail: boolean } | null {
    const needsDownload = !!watchedAttachment.attachment.urls?.download;
    const needsThumbnail = !!watchedAttachment.attachment.urls?.thumbnail;

    if (!needsDownload && !needsThumbnail) {
      return null;
    }

    return { needsDownload, needsThumbnail };
  }

  /**
   * Handles successful URL refresh by updating the store and rescheduling.
   */
  function handleRefreshSuccess(
    watchedAttachmented: WatchedAttachment,
    attachmentId: string,
    urls: MessageAttachmentUrlDTO,
  ): void {
    conversationStore.updateAttachmentUrls({
      conversationId: watchedAttachmented.conversationId,
      messageId: watchedAttachmented.messageId,
      attachmentId,
      urls,
    });

    watchedAttachmented.attachment.urls = urls;
    watchedAttachmented.retryCount = 0;
    watchedAttachmented.abortController = null;

    scheduleRefresh(attachmentId);
  }

  /**
   * Handles refresh failure by scheduling a retry with exponential backoff.
   */
  function handleRefreshFailure(
    watchedAttachment: WatchedAttachment,
    conversationId: string,
    attachmentId: string,
  ): void {
    watchedAttachment.retryCount++;

    if (watchedAttachment.retryCount < maxRefreshRetries) {
      const backoffDelay = 2 ** watchedAttachment.retryCount * 1000;
      watchedAttachment.timerId = setTimeout(() => {
        refreshConversationAttachment({ conversationId, attachmentId });
      }, backoffDelay);
    }
  }

  /**
   * Manually triggers a refresh of an attachment's URLs.
   *
   * Use this when a media element fails to load to immediately fetch fresh URLs.
   */
  async function refreshConversationAttachment({
    conversationId,
    attachmentId,
  }: AttachmentIdentifier): Promise<MessageAttachmentUrlDTO | null> {
    const cacheKey = `${conversationId}:${attachmentId}`;

    // Return existing pending refresh if one is in progress
    const pendingRefresh = getPendingRefresh(cacheKey);
    if (pendingRefresh) {
      return pendingRefresh;
    }

    const watchedAttachment = watchedAttachments.get(attachmentId);
    if (!watchedAttachment) {
      return null;
    }

    const refreshNeeds = getRefreshNeeds(watchedAttachment);
    if (!refreshNeeds) {
      return null;
    }

    const abortController = new AbortController();
    watchedAttachment.abortController = abortController;

    const refreshPromise = fetchFreshUrls({
      conversationId,
      attachmentId,
      ...refreshNeeds,
      signal: abortController.signal,
    });

    pendingRefreshes.set(cacheKey, refreshPromise);

    try {
      const urls = await refreshPromise;

      if (abortController.signal.aborted) {
        return null;
      }

      handleRefreshSuccess(watchedAttachment, attachmentId, urls);
      return urls;
    }
    catch {
      if (abortController.signal.aborted) {
        return null;
      }

      handleRefreshFailure(watchedAttachment, conversationId, attachmentId);
      return null;
    }
    finally {
      watchedAttachment.abortController = null;
      pendingRefreshes.delete(cacheKey);
    }
  }

  /**
   * Schedules the next refresh time
   */
  function scheduleRefresh(attachmentId: string): void {
    const watchedAttachment = watchedAttachments.get(attachmentId);
    if (!watchedAttachment) {
      return;
    }

    if (watchedAttachment.timerId) {
      clearTimeout(watchedAttachment.timerId);
      watchedAttachment.timerId = null;
    }

    const earliestExpiry = getEarliestExpiry(watchedAttachment.attachment);
    if (!earliestExpiry) {
      return;
    }

    const delay = calculateNextRefreshTime(earliestExpiry);

    watchedAttachment.timerId = setTimeout(() => {
      refreshConversationAttachment({ conversationId: watchedAttachment.conversationId, attachmentId });
    }, delay);
  }

  /**
   * Starts watching an attachment for automatic token refresh.
   *
   * When an attachment is watched, a timer is scheduled to refresh its URLs
   * before they expire (based on `refreshBufferMs`).
   */
  function watchAttachment({
    conversationId,
    messageId,
    attachment,
  }: WatchAttachmentParams): void {
    if (!hasRefreshableUrls(attachment)) {
      return;
    }

    const attachmentId = attachment.id;
    attachmentIds.add(attachmentId);

    const existing = watchedAttachments.get(attachmentId);
    if (existing) {
      existing.attachment = attachment;
      existing.refCount++;
      scheduleRefresh(attachmentId);
      return;
    }

    const watchedAttachment: WatchedAttachment = {
      conversationId,
      messageId,
      attachment,
      timerId: null,
      retryCount: 0,
      refCount: 1,
      abortController: null,
    };

    watchedAttachments.set(attachmentId, watchedAttachment);
    scheduleRefresh(attachmentId);
  }

  /**
   * Stops watching an attachment for this composable instance.
   *
   * Decrements the reference count for the attachment. When the count reaches
   * zero (no components are watching it), the refresh timer is cleared and
   * any pending refresh request is aborted.
   */
  function unwatchAttachment({ attachmentId }: UnwatchAttachmentParams): void {
    if (!attachmentIds.has(attachmentId)) {
      return;
    }
    attachmentIds.delete(attachmentId);

    const watchedAttachment = watchedAttachments.get(attachmentId);
    if (!watchedAttachment) {
      return;
    }

    watchedAttachment.refCount--;

    if (watchedAttachment.refCount <= 0) {
      watchedAttachment.abortController?.abort();

      if (watchedAttachment.timerId) {
        clearTimeout(watchedAttachment.timerId);
      }
      watchedAttachments.delete(attachmentId);
    }
  }

  /**
   * Manually cleans up all attachments watched by this composable instance.
   *
   * This is called automatically when the component unmounts via `onUnmounted`.
   * You only need to call this manually if you want to stop watching all
   * attachments before the component unmounts.
   */
  function cleanup(): void {
    for (const attachmentId of attachmentIds) {
      unwatchAttachment({ attachmentId });
    }
    attachmentIds.clear();
  }

  onUnmounted(() => {
    cleanup();
  });

  return {
    cleanup,
    refreshConversationAttachment,
    watchAttachment,
    unwatchAttachment,
  };
}
